import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * lazy() wrapper that recovers from deploy-time chunk mismatches.
 *
 * Hash-named chunk files are replaced on every deploy. A browser holding a
 * pre-deploy shell (open tab, cached HTML) requests chunk files the server no
 * longer serves, and React surfaces "Failed to fetch dynamically imported
 * module". One full reload pulls the fresh shell with matching hashes, so do
 * that automatically — guarded by a session flag so a genuinely broken deploy
 * cannot create a reload loop; the error then reaches the error boundary.
 */
const RELOAD_FLAG = 'lighthunt-chunk-reload';

export function lazyRoute<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    load().catch((error: unknown) => {
      if (typeof window === 'undefined') throw error;

      let alreadyRetried = true;
      try {
        alreadyRetried = sessionStorage.getItem(RELOAD_FLAG) === '1';
        if (!alreadyRetried) sessionStorage.setItem(RELOAD_FLAG, '1');
      } catch {
        // Private browsing can block sessionStorage; fall through without the
        // guard rather than swallowing the recovery.
        alreadyRetried = false;
      }

      if (!alreadyRetried) {
        window.location.reload();
      }

      throw error;
    }),
  );
}
